import type { ReactNode, RefObject } from 'react'
import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ButtonTone } from '../telegram/buttons.tsx'
import { useHideBottomButtons, useMainButton, useSecondaryButton } from '../telegram/buttons.tsx'
import { Button } from './Button.tsx'
import { IconClose } from './icons.tsx'

export interface SheetAction {
  text: string
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  tone?: ButtonTone
}

interface SheetProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  subtitle?: ReactNode
  /** Почти на весь экран (поиск людей и т.п.). */
  full?: boolean
  /** Главное действие — в Telegram MainButton (вне Telegram — кнопка внизу). */
  primary?: SheetAction
  secondary?: SheetAction
  children: ReactNode
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select, [tabindex]:not([tabindex="-1"])'

let openCount = 0

function useOverlayBehavior(onClose: () => void, panel: RefObject<HTMLDivElement | null>): void {
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })

  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null
    openCount += 1
    document.body.style.overflow = 'hidden'

    const el = panel.current
    const auto = el?.querySelector<HTMLElement>('[data-autofocus]')
    if (auto) auto.focus()
    else el?.focus()

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closeRef.current()
        return
      }
      // Простая ловушка фокуса внутри шторки
      if (e.key === 'Tab' && el) {
        const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)]
        const first = items[0]
        const last = items.at(-1)
        if (!first || !last) return
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      openCount -= 1
      if (openCount === 0) document.body.style.overflow = ''
      prevFocus?.focus?.()
    }
  }, [panel])
}

function SheetButtons({ primary, secondary }: { primary?: SheetAction; secondary?: SheetAction }) {
  useMainButton({
    text: primary?.text ?? '',
    onClick: () => primary?.onClick(),
    visible: Boolean(primary),
    disabled: primary?.disabled,
    loading: primary?.loading,
    tone: primary?.tone,
    priority: 1,
  })
  useSecondaryButton({
    text: secondary?.text ?? '',
    onClick: () => secondary?.onClick(),
    visible: Boolean(secondary),
    disabled: secondary?.disabled,
    loading: secondary?.loading,
    tone: secondary?.tone,
    priority: 1,
  })
  return null
}

function SheetPanel({
  onClose,
  title,
  subtitle,
  full,
  primary,
  secondary,
  children,
}: Omit<SheetProps, 'open'>) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useOverlayBehavior(onClose, panel)

  return (
    <div className="overlay">
      <button
        type="button"
        className="overlay__backdrop"
        aria-label="Закрыть"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panel}
        className={`sheet${full ? ' sheet--full' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <SheetButtons primary={primary} secondary={secondary} />
        <div className="sheet__handle" aria-hidden="true" />
        <div className="sheet__header">
          <div className="sheet__title" id={titleId}>
            {title}
            {subtitle && <div className="sheet__subtitle">{subtitle}</div>}
          </div>
          <button type="button" className="icon-btn" aria-label="Закрыть" onClick={onClose}>
            <IconClose size={22} />
          </button>
        </div>
        <div className="sheet__body">{children}</div>
      </div>
    </div>
  )
}

/** Нижняя шторка. Esc и тап по фону закрывают. */
export function Sheet(props: SheetProps) {
  if (!props.open) return null
  return createPortal(<SheetPanel {...props} />, document.body)
}

interface ConfirmProps {
  open: boolean
  title: string
  text?: ReactNode
  confirmText: string
  cancelText?: string
  danger?: boolean
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}

function ConfirmPanel({
  title,
  text,
  confirmText,
  cancelText = 'Отмена',
  danger,
  loading,
  onConfirm,
  onClose,
}: Omit<ConfirmProps, 'open'>) {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useOverlayBehavior(onClose, panel)
  useHideBottomButtons(true)
  return (
    <div className="overlay overlay--center">
      <button
        type="button"
        className="overlay__backdrop"
        aria-label="Закрыть"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panel}
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="dialog__title" id={titleId}>
          {title}
        </div>
        {text && <div className="dialog__text">{text}</div>}
        <div className="dialog__actions">
          <Button variant="secondary" onClick={onClose}>
            {cancelText}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={loading}
            onClick={onConfirm}
            data-autofocus
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Модальное подтверждение (вместо window.confirm). */
export function ConfirmDialog(props: ConfirmProps) {
  if (!props.open) return null
  return createPortal(<ConfirmPanel {...props} />, document.body)
}
